/**
 * /forecast — РЕДИЗАЙН (крок 1). Токен-каркас + нові KPI-плитки. Логіку даних
 * (6 джерел + кеш) скопійовано зі старої сторінки без змін, тож цифри й поведінка
 * ті самі. Важкі ForecastMainChart / DeviationTable поки переюзано як є —
 * перетемлення на токени й серії brandbook буде наступним кроком.
 *
 * Ізоляція: рендериться лише коли VITE_REDESIGN='1' (route-swap у config.tsx),
 * тож прод бачить стару сторінку.
 */
import i18n from '@/i18n';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { OptTabsRightSlot, useOptSectionsVisible } from '@/components/feature/OptSectionTabs';
import { getStoredUser } from '@/hooks/useAuth';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useForecastUi } from '@/hooks/useForecastUi';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import {
  GenerationForecastLatestResponse, GenerationForecastDayResponse, PvActualDayResponse,
  ForecastComparisonResponse, ForecastHalfHourResponse, getDayTabLabels, fmtDateTimeLocal,
} from '@/mocks/generationForecast';
import ForecastMainChart from './ForecastMainChartRd';
import CurtailmentBanner from '../components/CurtailmentBanner';
import DeviationTable from './DeviationTableRd';
import ForecastExportModal from '../components/ForecastExportModal';
import ForecastKpiRd from './ForecastKpiRd';
import WeatherMap from './WeatherMap';

const REFRESH_MS = 2 * 60 * 1000;
const CACHE_TTL_MS = 2 * 60 * 1000;

function readCache<T>(key: string): T | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('helios:' + key) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data as T;
  } catch { return null; }
}
function writeCache(key: string, data: unknown) {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem('helios:' + key, JSON.stringify({ ts: Date.now(), data })); } catch { /* noop */ }
}

// --- Погодний чіп на токенах ---
function pickSky(w: any): { icon: string; label: string } {
  if (!w) return { icon: 'ri-question-line', label: '—' };
  const day = (w.shortwaveRadiation ?? 0) > 5;
  const cc = w.cloudCover ?? 0; const pr = w.precipitation ?? 0;
  if (pr >= 2.5) return { icon: 'ri-showers-line', label: i18n.t('ak_fc_rain') };
  if (pr >= 0.2) return { icon: 'ri-drizzle-line', label: i18n.t('ak_fc_drizzle') };
  if (cc < 15) return { icon: day ? 'ri-sun-line' : 'ri-moon-clear-line', label: i18n.t('ak_fc_clear') };
  if (cc < 50) return { icon: day ? 'ri-sun-cloudy-line' : 'ri-moon-cloudy-line', label: i18n.t('ak_fc_partly_cloudy') };
  if (cc < 85) return { icon: 'ri-cloudy-line', label: i18n.t('ak_fc_cloudy') };
  return { icon: 'ri-cloudy-2-line', label: i18n.t('ak_fc_overcast') };
}
const hhmm = (v: any): string | null => (!v || typeof v !== 'string' ? null : (v.indexOf('T') >= 0 ? v.slice(v.indexOf('T') + 1) : v).slice(0, 5) || null);

function WeatherChip({ weather, sun }: { weather: any; sun?: { sunrise?: string; sunset?: string } | null }) {
  const { t } = useTranslation();
  const sky = pickSky(weather);
  const temp = weather?.temperature2m;
  const windMs = weather?.windSpeed10m != null ? weather.windSpeed10m / 3.6 : null;
  const sunrise = hhmm(sun?.sunrise); const sunset = hhmm(sun?.sunset);
  return (
    <div className="flex items-center gap-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.32)' }} title={t('ak_fc_current_weather', { sky: sky.label })}>
      <i className={sky.icon + ' text-base'} style={{ color: 'var(--text-muted)' }}></i>
      <span className="text-xs text-muted">{sky.label}</span>
      <span className="text-dim">·</span>
      <span className="text-sm font-semibold text-ink font-display">{temp != null ? Math.round(temp) + '°C' : '—'}</span>
      <span className="text-dim">·</span>
      <i className="ri-windy-line text-sm" style={{ color: 'var(--text-dim)' }}></i>
      <span className="text-xs text-muted">{windMs != null ? windMs.toFixed(1) + ' ' + i18n.t('ak_fc_m_s') : '—'}</span>
      {sunrise && <><span className="text-dim">·</span><span className="text-xs text-muted">↑{sunrise}</span></>}
      {sunset && <span className="text-xs text-muted">↓{sunset}</span>}
    </div>
  );
}

export default function ForecastRedesignedPage() {
  const navigate = useNavigate();
  // Чи стоїть над сторінкою ряд кнопок-розділів (тільки ArtemK).
  const optTabsVisible = useOptSectionsVisible();
  const { t } = useTranslation();
  const user = getStoredUser();

  const dayTabs = useMemo(() => getDayTabLabels(), []);
  // Дата — зі спільного стану шапки (керується з топбара). Експорт — по nonce.
  const { date: selectedDate, exportNonce } = useForecastUi();
  // Заморожений на D-2 прогноз обраної доби — другий стан плитки «Прогнозована
  // генерація» (що було відомо на момент подання графіка до MMS).
  const [frozenForecast, setFrozenForecast] = useState<{ pvKwh: number | null; frozenAt: string | null } | null>(null);
  const [mode, setMode] = useState<'hourly' | '30min'>('30min');
  const [showExportModal, setShowExportModal] = useState(false);

  const [latest, setLatest] = useState<GenerationForecastLatestResponse | null>(null);
  const [dayData, setDayData] = useState<GenerationForecastDayResponse | null>(null);
  const [pvActualData, setPvActualData] = useState<PvActualDayResponse | null>(null);
  const [comparisonData, setComparisonData] = useState<ForecastComparisonResponse | null>(null);
  const [halfHourData, setHalfHourData] = useState<ForecastHalfHourResponse | null>(null);
  const [ecmwfShadowData, setEcmwfShadowData] = useState<{ hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null>(null);
  const [latestLoading, setLatestLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(true);
  const [pvLoading, setPvLoading] = useState(true);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [halfHourLoading, setHalfHourLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('—');
  const { selectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id ?? '';
  // Координати станції для погодної карти (Windy) — окремий легкий запит,
  // бо selectedAsset у localStorage їх не несе.
  const [assetLoc, setAssetLoc] = useState<{ lat: number; lon: number } | null>(null);
  useEffect(() => {
    if (!assetId) { setAssetLoc(null); return; }
    let alive = true;
    api.get<{ latitude?: number | null; longitude?: number | null }>(`/assets/${assetId}`)
      .then((r) => {
        if (!alive) return;
        setAssetLoc(r.latitude != null && r.longitude != null ? { lat: r.latitude, lon: r.longitude } : null);
      })
      .catch(() => { if (alive) setAssetLoc(null); });
    return () => { alive = false; };
  }, [assetId]);

  // Кожен тип запиту має свій AbortController: новий запит того ж типу (інша дата,
  // інша станція, оновлення за таймером) скасовує попередній. Інакше пізня
  // відповідь за попередню дату перезаписує графік поточної, а сервер рахує зайве.
  const inflight = useRef<Record<string, AbortController>>({});
  const beginRequest = useCallback((slot: string) => {
    inflight.current[slot]?.abort();
    const ctrl = new AbortController();
    inflight.current[slot] = ctrl;
    return ctrl.signal;
  }, []);

  const fetchLatest = useCallback(async () => {
    if (!assetId) { setLatestLoading(false); return; }
    const signal = beginRequest('latest');
    setLatestLoading(true);
    try {
      const result = await api.get<GenerationForecastLatestResponse>(`/forecasting/assets/${assetId}/generation-forecasts/latest?includeHourly=false`, signal);
      if (signal.aborted) return;
      setLatest(result); setLastUpdated(fmtDateTimeLocal(result.forecastRunAt) ?? '—'); setError(null);
    } catch (err) {
      if (signal.aborted) return;
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) setLatest(null);
      else { setLatest(null); setLastUpdated('—'); if (!isNet) setError(getApiErrorMessage(err, 'Failed to load forecast summary')); }
      if (isNet) setDemoMode(true);
    } finally { if (!signal.aborted) setLatestLoading(false); }
  }, [assetId, beginRequest]);

  const fetchDay = useCallback(async (date: string) => {
    if (!assetId) { setDayLoading(false); return; }
    const signal = beginRequest('day');
    const key = `generation-forecasts-day:${assetId}:${date}`;
    const cached = readCache<GenerationForecastDayResponse>(key);
    if (cached) { setDayData(cached); setDayLoading(false); }
    setDayLoading(true);
    try {
      const result = await api.get<GenerationForecastDayResponse>(`/forecasting/assets/${assetId}/generation-forecasts/day?date=${date}`, signal);
      if (signal.aborted) return;
      setDayData(result); writeCache(key, result);
    } catch (err) {
      if (signal.aborted) return;
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) setDayData(null);
      else { setDayData(null); if (!isNet) setError(getApiErrorMessage(err, 'Failed to load daily forecast')); }
      if (isNet) setDemoMode(true);
    } finally { if (!signal.aborted) setDayLoading(false); }
  }, [assetId, beginRequest]);

  const fetchPvActual = useCallback(async (date: string) => {
    if (!assetId) { setPvLoading(false); return; }
    const signal = beginRequest('pv');
    const key = `pv-actuals-day:${assetId}:${date}`;
    const cached = readCache<PvActualDayResponse>(key);
    if (cached) { setPvActualData(cached); setPvLoading(false); }
    setPvLoading(true);
    try {
      const [result, hourlyResp] = await Promise.all([
        api.get<PvActualDayResponse>(`/forecasting/assets/${assetId}/pv-actuals/day?date=${date}&includeCurrent=true`, signal),
        api.get<{ hourly: Array<{ hour: string; actualPacKw: number | null }> }>(`/forecasting/assets/${assetId}/hourly-actuals/day?date=${date}`, signal).catch(() => ({ hourly: [] as Array<{ hour: string; actualPacKw: number | null }> })),
      ]);
      if (signal.aborted) return;
      try {
        const have = new Set<string>();
        for (const r of (result?.actual ?? [])) if (r && (r as any).hour) have.add((r as any).hour);
        for (const h of (hourlyResp?.hourly ?? [])) {
          if (!h.hour || h.actualPacKw == null || have.has(h.hour)) continue;
          (result.actual as any[]).push({ hour: h.hour, samplePosition: 'H+30', status: 'past', isFinal: true, actualPacKw: Number(h.actualPacKw), qualityFlag: 'hourly_avg_fallback' });
        }
        (result.actual as any[]).sort((a: any, b: any) => String(a.hour || '').localeCompare(String(b.hour || '')));
      } catch (e) { console.warn('[fetchPvActual] merge failed:', e); }
      setPvActualData(result); writeCache(key, result);
    } catch (err) {
      if (signal.aborted) return;
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) setPvActualData(null);
      else { setPvActualData(null); if (!isNet) setError(getApiErrorMessage(err, 'Failed to load PV actuals')); }
      if (isNet) setDemoMode(true);
    } finally { if (!signal.aborted) setPvLoading(false); }
  }, [assetId, beginRequest]);

  const fetchComparison = useCallback(async (date: string) => {
    if (!assetId) { setComparisonLoading(false); return; }
    const signal = beginRequest('comparison');
    const key = `forecast-comparison-day:${assetId}:${date}`;
    const cached = readCache<ForecastComparisonResponse>(key);
    if (cached) { setComparisonData(cached); setComparisonLoading(false); } else setComparisonLoading(true);
    try {
      const result = await api.get<ForecastComparisonResponse>(`/forecasting/assets/${assetId}/forecast-comparison/day?date=${date}`, signal);
      if (signal.aborted) return;
      setComparisonData(result); writeCache(key, result);
    } catch (err) {
      if (signal.aborted) return;
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) setComparisonData(null);
      else { setComparisonData(null); if (!isNet) setError(getApiErrorMessage(err, 'Failed to load forecast comparison')); }
      if (isNet) setDemoMode(true);
    } finally { if (!signal.aborted) setComparisonLoading(false); }
  }, [assetId, beginRequest]);

  const fetchHalfHour = useCallback(async (date: string) => {
    if (!assetId) { setHalfHourLoading(false); return; }
    const signal = beginRequest('halfHour');
    const key = `forecast-comparison-half-hour:${assetId}:${date}`;
    const cached = readCache<ForecastHalfHourResponse>(key);
    if (cached) { setHalfHourData(cached); setHalfHourLoading(false); } else setHalfHourLoading(true);
    try {
      const result = await api.get<ForecastHalfHourResponse>(`/forecasting/assets/${assetId}/forecast-comparison/half-hour?date=${date}`, signal);
      if (signal.aborted) return;
      setHalfHourData(result); writeCache(key, result);
    } catch (err) {
      if (signal.aborted) return;
      const isNet = err instanceof ApiError && err.status === 0;
      if (err instanceof ApiError && err.status === 404) setHalfHourData(null);
      else { setHalfHourData(null); if (!isNet) setError(getApiErrorMessage(err, 'Failed to load 30-min forecast comparison')); }
      if (isNet) setDemoMode(true);
    } finally { if (!signal.aborted) setHalfHourLoading(false); }
  }, [assetId, beginRequest]);

  const fetchEcmwfShadow = useCallback(async (date: string) => {
    if (!assetId) return;
    const signal = beginRequest('ecmwf');
    try {
      const result = await api.get<{ hourly: Array<{ hour: string; forecastPowerKw: number | null }> }>(`/forecasting/assets/${assetId}/ecmwf-shadow/day?date=${date}`, signal);
      if (signal.aborted) return;
      setEcmwfShadowData(result);
    } catch { if (!signal.aborted) setEcmwfShadowData(null); }
  }, [assetId, beginRequest]);

  useEffect(() => {
    fetchLatest(); fetchDay(selectedDate); fetchPvActual(selectedDate);
    fetchComparison(selectedDate); fetchHalfHour(selectedDate); fetchEcmwfShadow(selectedDate);
    setFrozenForecast(null);
    const frozenSignal = beginRequest('frozen');
    api.get<{ hours: number; pvKwh: number | null; frozenAt: string | null }>(
      `/forecasting/assets/${assetId}/frozen-forecast?date=${selectedDate}`, frozenSignal)
      .then((d) => { if (!frozenSignal.aborted) setFrozenForecast(d && d.hours ? { pvKwh: d.pvKwh, frozenAt: d.frozenAt } : null); })
      .catch(() => { if (!frozenSignal.aborted) setFrozenForecast(null); });
    const id = setInterval(() => {
      fetchLatest(); fetchDay(selectedDate); fetchPvActual(selectedDate);
      fetchComparison(selectedDate); fetchHalfHour(selectedDate);
    }, REFRESH_MS);
    return () => {
      clearInterval(id);
      Object.values(inflight.current).forEach((c) => c.abort());
    };
  }, [fetchLatest, fetchDay, fetchPvActual, fetchComparison, fetchHalfHour, fetchEcmwfShadow, beginRequest, selectedDate]);

  const selectedTab = dayTabs.find((d) => d.apiDate === selectedDate);
  const isAnyLoading = latestLoading || dayLoading || pvLoading || comparisonLoading || halfHourLoading;

  const nowWeather = useMemo(() => {
    const hourly = dayData?.hourly ?? [];
    if (!hourly.length) return null;
    const nowKey = new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Kyiv' });
    const cur = nowKey.slice(0, 2) + ':00';
    const match = hourly.find((h) => h.hour === cur && h.weather);
    return match?.weather ?? [...hourly].reverse().find((h) => h.weather)?.weather ?? null;
  }, [dayData]);

  // Топбар просить експорт через інкремент nonce.
  useEffect(() => { if (exportNonce > 0) setShowExportModal(true); }, [exportNonce]);

  if (!user) { navigate('/login'); return null; }

  return (
    <DashboardLayout>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1400)}`}>

          {/* Заголовок і керування добою/експортом винесено в топбар (isForecastRd).
              У тілі — лише погодний чіп слим-смужкою.
              Коли над сторінкою стоїть ряд кнопок-розділів (меню ArtemK), чіп
              їде в правий слот того рядка: окрема смужка під кнопками з'їдала
              висоту й дублювала горизонталь. Для решти акаунтів усе як було. */}
          {nowWeather && (
            optTabsVisible
              ? <OptTabsRightSlot><WeatherChip weather={nowWeather} sun={dayData?.sun} /></OptTabsRightSlot>
              : (
                <div className="mb-5 flex flex-wrap gap-3">
                  <WeatherChip weather={nowWeather} sun={dayData?.sun} />
                </div>
              )
          )}

          {/* Смужку «Завантаження даних…» прибрано: вона з'являлась/зникала під
              погодним чипом і смикала лейаут. Стан завантаження показує сам графік
              у своїй фіксованій плитці. */}
          {error && !isAnyLoading && (
            <div className="tile p-3 mb-5 flex items-center gap-2 text-sm" style={{ borderColor: 'var(--err)' }}>
              <i className="ri-error-warning-line text-base" style={{ color: 'var(--err)' }}></i>
              <span className="text-ink">{t('fc_load_error_title')}</span><span className="text-muted text-xs">{error}</span>
            </div>
          )}
          {demoMode && !error && !isAnyLoading && (
            <div className="tile p-2.5 mb-5 flex items-center gap-2 text-xs text-muted" style={{ boxShadow: 'none' }}>
              <i className="ri-wifi-off-line text-sm"></i>{t('fc_demo_banner')}
            </div>
          )}

          <div className="mb-5">
            <ForecastKpiRd latest={latest} loading={latestLoading} pvActualData={pvActualData}
              ecmwfShadowData={ecmwfShadowData} comparisonData={comparisonData} dayData={dayData}
              selectedDate={selectedDate} frozenForecast={frozenForecast} />
          </div>

          {/* Погодна карта — окремою повношириною плиткою, щоб не звужувати
              KPI-плитки вище. Windy Map Forecast API (JS, з ключем), карта
              монтується лише коли відомі координати станції. */}
          {assetLoc && (
            <div className="tile mb-5" style={{ height: 293, overflow: 'hidden', position: 'relative' }}>
              <WeatherMap lat={assetLoc.lat} lon={assetLoc.lon} height="100%" overlay="clouds" zoom={13} />
            </div>
          )}

          {/* Графік (переюз, перетема — наступний крок) */}
          <div className="mb-5">
            <CurtailmentBanner active={halfHourData?.activeCurtailment} assetName={selectedAsset?.assetName} />
            <ForecastMainChart
              comparisonData={comparisonData} halfHourData={halfHourData} ecmwfShadowData={ecmwfShadowData}
              pvActualData={pvActualData} mode={mode}
              dayLabel={selectedTab ? (selectedTab.label === 'Today' ? t('fc_today') : selectedTab.label) : selectedDate}
              dayDisplay={selectedTab?.displayDate ?? selectedDate}
              loading={mode === 'hourly' ? comparisonLoading : halfHourLoading} onModeChange={setMode} />
          </div>

          {/* Таблиця відхилень (переюз) */}
          <div className="mb-5">
            <DeviationTable dayData={dayData} pvActualData={pvActualData} ecmwfShadowData={ecmwfShadowData}
              dayLabel={selectedTab ? (selectedTab.label === 'Today' ? t('fc_today') : selectedTab.label) : selectedDate}
              loading={dayLoading || pvLoading}
              onRefresh={() => { fetchLatest(); fetchDay(selectedDate); fetchPvActual(selectedDate); fetchComparison(selectedDate); fetchHalfHour(selectedDate); }} />
          </div>

          <div className="mt-6 text-xs text-dim">
            {t('fc_footer_updated')}: {lastUpdated} · {t('fc_footer_model')}: {latest?.modelVersion ?? '—'} · {t('fc_footer_refresh')}
          </div>
        </div>
      </div>

      {showExportModal && (
        <ForecastExportModal assetId={assetId} defaultDateFrom={selectedDate} defaultDateTo={selectedDate} onClose={() => setShowExportModal(false)} />
      )}
    </DashboardLayout>
  );
}
